import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../utils/cn';
import { FieldTooltip } from './FieldTooltip';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  searchable?: boolean;
  placement?: 'above' | 'below';
  className?: string;
  tooltip?: string;
}

const ChevronDown = () => (
  <svg
    className="h-4 w-4 flex-shrink-0 text-heritage-grey"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CheckIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

export const Select: React.FC<SelectProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Select...',
  label,
  searchable = true,
  placement = 'below',
  className,
  tooltip,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedOption = options.find((o) => o.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;
  const showSearch = searchable && options.length > 5;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {label && (
        <div className="mb-1 flex items-center">
          <label className="block text-[13px] font-medium text-text-grey">{label}</label>
          {tooltip && <FieldTooltip text={tooltip} />}
        </div>
      )}
      <button
        type="button"
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border border-[#E2E5EA] bg-white px-3 py-2.5 text-left text-[13px]',
          'focus:outline-none focus:border-blueprint-blue',
          'transition-colors hover:border-heritage-grey',
          value ? 'text-text-grey' : 'text-heritage-grey'
        )}
        onClick={() => {
          setOpen((prev) => !prev);
          if (open) setQuery('');
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{displayValue}</span>
        <span className={cn('transition-transform duration-150', open && 'rotate-180')}>
          <ChevronDown />
        </span>
      </button>

      {open && (
        <div
          className={cn(
            'absolute left-0 right-0 z-[100] min-w-[140px] rounded-lg border border-[#E2E5EA] bg-white shadow-lg',
            placement === 'above' ? 'bottom-full mb-1' : 'top-full mt-1'
          )}
          role="listbox"
        >
          {showSearch && (
            <div className="border-b border-[#EEF0F3] px-2 py-1.5">
              <input
                type="text"
                className="w-full rounded-md border border-[#E2E5EA] px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-blueprint-blue"
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onPointerDown={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div className="max-h-52 overflow-y-auto py-1">
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-[13px] text-heritage-grey">No matches</div>
            )}
            {filteredOptions.map((option) => {
              const selected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-2 text-left text-[13px]',
                    'hover:bg-page-bg transition-colors',
                    selected
                      ? 'bg-hero-red-10 text-readable-red font-medium'
                      : 'text-text-grey'
                  )}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  <span>{option.label}</span>
                  {selected && (
                    <span className="text-readable-red">
                      <CheckIcon />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
